import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  asapScheduler,
  defer,
  finalize,
  from,
  Observable,
  of,
  Subject,
  subscribeOn,
  Subscription,
  takeUntil,
} from 'rxjs';
import { ExtractExpr, ItemExprUtils } from '../../utils/item-expr.utils';
import { WidthType } from '../dropdown/dropdown.component';

@Component({
  selector: 'app-select-box',
  templateUrl: 'select-box.component.html',
  styleUrls: ['select-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectBoxComponent,
    },
  ],
})
export class SelectBoxComponent
  implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy
{
  text: string = '';
  _currentText: string = '';
  _searchText: string = '';

  @Input()
  value: any;
  @Output()
  valueChange = new EventEmitter<any>();
  @Output()
  onItemSelected = new EventEmitter<any>();

  @Output()
  onSearchValueChanged = new EventEmitter<string>();

  @Output()
  onKeydown = new EventEmitter<any>();

  @Input()
  opened = false;
  @Output()
  openedChange = new EventEmitter<boolean>();

  @Input()
  items: any[];
  @Input()
  dataSource:
    | any[]
    | Observable<any[]>
    | Promise<any[]>
    | ((searchText?: string) => any[] | Observable<any[]> | Promise<any[]>);

  _resultItems: any[] = [];

  @Input()
  searchEnabled = false;
  @Input()
  searchCaseSensitiveEnabled = true;
  @Input()
  searchFilterFn: (
    searchText: string,
    displayText: string,
    item: any
  ) => boolean;

  @Input()
  keepSearchTextEnabled = false;
  @Input()
  minSearchLength = 0;
  @Input()
  searchDebounce = 0;
  @Input()
  openOnInputClick = true;

  @Input()
  notEmpty = false;

  @Input()
  name: string;

  @Input()
  label: string;

  @Input()
  placeholder: string;

  @Input()
  readOnly: boolean;
  @Input()
  disabled: boolean;

  @Input()
  showErrorEnabled: boolean = true;
  @Input()
  showClearButton: boolean = true;
  @Input()
  showOpenedIndicatorButton: boolean = true;

  @Input()
  dropdownWidth: string = WidthType.SyncWithTarget;

  @Output()
  onClearClick = new EventEmitter<any>();

  private onChange = (value) => {};

  private onTouched = () => {};

  private selectedIndex: number = -1;

  @Input()
  valueExpr: ExtractExpr;
  @Input()
  displayExpr: ExtractExpr;
  @Input()
  textDisplayExpr: ExtractExpr;

  private searchResultsSubscription: Subscription;
  searchingResults = true;

  private lifecycle = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchEnabled && !this.searchEnabled) {
      this.searchResultsSubscription?.unsubscribe();
    }

    let itemsListUpdateRequested = false;

    if (changes.value || changes.notEmpty) {
      this.searchResults(null, () => {
        this.handleSelectValueIfCan(!changes.value?.firstChange);
      });
      itemsListUpdateRequested = true;
    } else if (changes.items || changes.dataSource) {
      this.searchResults(null, () => {
        this.handleSelectValueIfCan();
      });
      itemsListUpdateRequested = true;
    } else if (changes.valueExpr) {
      this.handleSelectValueIfCan();
    } else if (changes.textDisplayExpr || changes.displayExpr) {
      this.handleSelectText();
    }

    if (
      !itemsListUpdateRequested &&
      (changes.displayExpr ||
        changes.searchCaseSensitiveEnabled ||
        changes.searchFilterFn)
    ) {
      this.searchResults(this.text);
    }

    if ((changes.readOnly || changes.disabled) && this.opened) {
      this.opened = !(this.disabled || this.readOnly);
    }
  }

  ngAfterViewInit(): void {
    this.handleSelectValueIfCan();
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.searchResultsSubscription?.unsubscribe();
  }

  writeValue(value: any): void {
    if (this.value !== value) {
      this.value = value;

      if (!this.searchingResults) {
        this.handleSelectValueIfCan();
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputValue(e) {
    if (!this.searchEnabled) {
      return;
    }

    const text: string = e?.event?.target?.value ?? '';
    this.text = text;
    this._searchText = text;

    this.onSearchValueChanged.emit(text);

    if (text?.length >= this.minSearchLength) {
      this.searchResults(text, () => {
        this.opened = true;
      });
    } else {
      this.searchResultsSubscription?.unsubscribe();
      this.opened = false;
    }
  }

  onInputClear() {
    this.onClearClick.emit();

    this.onInputValue(null);

    this._searchText = null;
    this.selectedIndex = -1;
    this.emitSelectedValue();
  }

  onInputBoxClick() {
    if (
      this.openOnInputClick &&
      !this.disabled &&
      !this.readOnly &&
      !this.opened
    ) {
      this.opened = true;
    }
  }

  onInputFocus(focus: boolean) {
    if (focus && this.keepSearchTextEnabled && this._searchText) {
      if (this._resultItems?.length) {
        this.opened = true;
      }
    }
  }

  onBtnToggleClick() {
    if (!this.disabled && !this.readOnly && !this.opened) {
      this.opened = true;
    } else {
      this.opened = false;
    }
  }

  onListItemClick(e, item: any, index: number, key: any) {
    if (key !== this.value) {
      this._searchText = null;
      this.selectedIndex = index;
      this.handleSelectText();
      this.emitSelectedValue();
    } else if (!this.keepSearchTextEnabled) {
      this.text = this._currentText;
      this._searchText = null;
    }

    this.opened = false;
  }

  onDropdownOpenChange(opened: boolean) {
    this.opened = opened;
    if (!this.opened) {
      if (!this.keepSearchTextEnabled) {
        this.text = this._currentText;
        this._searchText = null;
      }
    }

    this.openedChange.emit(opened);
  }

  extractKey(item: any, index: number) {
    return this.extractExpr(item, index, this.valueExpr);
  }

  extractDisplay(item: any, index: number) {
    return this.extractExpr(item, index, this.displayExpr);
  }

  extractTextDisplay(item: any, index: number) {
    return this.extractExpr(
      item,
      index,
      this.textDisplayExpr ?? this.displayExpr
    );
  }

  private handleSelectValueIfCan(forceEmit = false) {
    let selectedIndex =
      this._resultItems?.findIndex((item, index) => {
        return this.extractKey(item, index) === this.value;
      }) ?? -1;

    if (this.notEmpty && this.items?.length && selectedIndex < 0) {
      selectedIndex = 0;
    }

    const shouldEmit = this.selectedIndex !== selectedIndex;

    this.selectedIndex = selectedIndex;

    this.handleSelectText();

    if (shouldEmit || forceEmit) {
      this.emitSelectedValue(forceEmit);
    }
  }

  private emitSelectedValue(forceEmit = false) {
    const item = this._resultItems?.[this.selectedIndex] ?? null;

    const value =
      this.selectedIndex < 0 ? null : this.extractKey(item, this.selectedIndex);

    if (this.value !== value || forceEmit) {
      this.handleSelectText();

      if (this.value !== value) {
        this.value = value;
        this.valueChange.emit(this.value);
      }

      this.onChange?.(value);
      this.onItemSelected.emit(item);
    }
  }

  private handleSelectText() {
    const item = this._resultItems?.[this.selectedIndex] ?? null;
    this.text = this._currentText =
      this.extractTextDisplay(item, this.selectedIndex) ?? '';
  }

  private extractExpr(item: any, index: number, expr: ExtractExpr) {
    if (expr) {
      return ItemExprUtils.extractValue(item, index, expr);
    }

    return item;
  }

  private searchResults(text: string, onSuccess?: (items: any[]) => void) {
    this.searchResultsSubscription?.unsubscribe();
    this.searchResultsSubscription = defer(() => {
      this.searchingResults = true;

      let aux = this.dataSource;

      if (typeof aux === 'function') {
        aux = aux(text);
      }

      if (!aux) {
        aux = this.items;

        if (typeof text === 'string') {
          const filterFn =
            this.searchFilterFn?.bind(this) ??
            ((searchText, displayText) => {
              return displayText?.includes?.(searchText);
            });

          if (!this.searchCaseSensitiveEnabled) {
            text = text.toLowerCase();
          }

          const extractDisplayFn = this.searchCaseSensitiveEnabled
            ? (item, index) => {
                return this.extractDisplay(item, index);
              }
            : (item, index) => {
                return this.extractDisplay(item, index)?.toLowerCase();
              };

          aux =
            this.items?.filter((item, index) => {
              const displayText: string = extractDisplayFn(item, index);
              return filterFn(text, displayText, item);
            }) ?? [];
        }
      }

      if (Array.isArray(aux)) {
        aux = of(aux);
      } else if (aux instanceof Promise) {
        aux = from(aux);
      }

      if (aux instanceof Observable) {
        return aux;
      } else {
        return of([]);
      }
    })
      .pipe(
        subscribeOn(asapScheduler, this.searchDebounce ?? 0),
        finalize(() => {
          this.searchingResults = false;
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (items) => {
          this._resultItems = items;

          this.searchingResults = false;

          onSuccess?.(items);
        },
      });
  }
}
